import type { ModelGuide } from "./types";

const UPDATED = "2026-05-14";

export const MODEL_GUIDES: ModelGuide[] = [
  {
    slug: "jaguar-e-type",
    make: "Jaguar",
    model: "E-Type",
    era: "1961–1975",
    metaTitle: "Jaguar E-Type Hire: History, Specs, and What It's Like to Drive",
    metaDescription:
      "A reference guide to the Jaguar E-Type: production history, specs by series, cultural appearances, what it's like to drive, and when to hire one for a wedding, film, or weekend tour.",
    tldr:
      "The Jaguar E-Type (1961–1975) is the archetypal British grand tourer: long bonnet, twin-cam straight-six (later V12), independent rear suspension, and looks Enzo Ferrari called the most beautiful car ever made. Three series across roadster, fixed-head coupé, and 2+2 bodies. Best suited to fine-weather driving, weddings, and photography rather than long motorway hauls.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/1039740/1d421a9e-03c1-41ad-ab2b-1510f35d8200_1280w.webp",
      alt: "Classic Jaguar E-Type roadster parked on a country road",
    },
    specs: [
      { label: "Years produced", value: "1961–1975" },
      { label: "Series", value: "I (1961–1968), II (1968–1971), III (1971–1975)" },
      { label: "Engine: Series I & II", value: "3.8 / 4.2 L XK twin-cam straight-six" },
      { label: "Engine: Series III", value: "5.3 L V12, SOHC per bank" },
      { label: "Power", value: "265 bhp (3.8 six) / 272 bhp (V12, gross figures)" },
      { label: "Top speed (period claim)", value: "≈ 150 mph / 241 km/h" },
      { label: "Body styles", value: "Roadster (OTS), Fixed-Head Coupé (FHC), 2+2 (from 1966)" },
      { label: "Drivetrain", value: "Front engine, rear-wheel drive, 4-speed manual (auto on V12)" },
      { label: "Notable features", value: "Monocoque with front subframe, independent rear suspension, all-round disc brakes" },
    ],
    sections: [
      {
        heading: "History",
        body:
          "Launched at the 1961 Geneva Motor Show, the E-Type was Jaguar's road-going successor to the Le Mans–winning D-Type. It packaged independent rear suspension and disc brakes, both unusual at the time, into a body designed by aerodynamicist Malcolm Sayer. Series I cars (1961–1968) used a 3.8 L and later 4.2 L XK six. Series II (1968–1971) introduced US-friendly bumpers, larger lights, and a more comfortable cabin. Series III (1971–1975) moved to a 5.3 L V12 with a longer wheelbase, available as roadster and 2+2.",
      },
      {
        heading: "What it's like to drive",
        body:
          "An E-Type is fast in a 1960s sense: torquey enough to feel modern from a standstill, but unassisted steering, a heavy clutch, and Moss (early) or all-synchro (1965+) gearboxes ask for deliberate inputs. The cabin is narrow, the screen low, and the driving position long-armed. The reward is the noise: the XK six has a hard-edged metallic howl, while the V12 has a creamy turbine sweep. Best on B-roads in dry weather; long motorway runs are tiring and exposed roadsters can be loud above 70 mph.",
      },
      {
        heading: "What occasions it suits",
        body:
          "The E-Type is a top three wedding car in the UK and a perennial film and TV vehicle. It photographs beautifully from any angle and looks credible in any era from the early 1960s to the mid-1970s. Couples often hire one for short transfers between ceremony and reception; production companies hire them for period drama and lifestyle commercials. As a driving experience, it suits a one- to three-day Cotswolds or Highland tour better than a week-long European trip.",
      },
    ],
    culture: [
      "Enzo Ferrari reportedly called the E-Type 'the most beautiful car ever made'.",
      "A 3.8 roadster is in the permanent collection of New York's Museum of Modern Art (MoMA).",
      "Driven by Diana Rigg's Emma Peel in The Avengers, by Austin Powers, and by Jay Kay of Jamiroquai (Series I roadster).",
      "Princes William and Harry rode in an E-Type Concept Zero (electric conversion) after Harry's 2018 wedding.",
    ],
    occasions: [
      "Wedding car (ceremony arrival or short transfers)",
      "Film & TV picture car (1960s–1970s period)",
      "Birthday and anniversary driving experiences",
      "Fashion and lifestyle photo shoots",
      "Concours and rally entries (Tour Auto, Mille Miglia Storica via 2+2)",
    ],
    faqs: [
      {
        q: "How much does it cost to hire a Jaguar E-Type?",
        a: "Typical day rates on BookAclassic range from £450 to £1,200, depending on series, body style, condition, and whether you take it self-drive or chauffeured. Roadsters and matching-numbers Series I cars sit at the top of the range.",
      },
      {
        q: "Can I drive an E-Type myself?",
        a: "Yes, many owners offer self-drive subject to age (usually 30+), licence checks, and a security deposit. Some Series I cars and concours examples are available only with a chauffeur; the listing will state which.",
      },
      {
        q: "Is the Jaguar E-Type reliable for a long road trip?",
        a: "An E-Type in good order is reasonably reliable for one- to three-day trips, but it's a 50–60-year-old car. Plan for shorter daily distances (≤200 miles), avoid heavy motorway use in summer (cooling) and check the owner's preferred touring radius before booking.",
      },
      {
        q: "Series I, II, or III: which should I hire?",
        a: "Series I is the purest design and the most valuable. Series II is the easiest to live with day-to-day (better cooling, brakes, ergonomics). Series III V12 is the most relaxed grand tourer but heavier and thirstier. For a wedding pick Series I or II roadster; for a long drive consider Series II FHC or III 2+2.",
      },
    ],
    listingsQuery: { make: "Jaguar", q: "E-Type" },
    related: {
      models: ["aston-martin-db5", "porsche-911-air-cooled", "mercedes-pagoda"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      cities: ["london", "cotswolds"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "ford-mustang-1967",
    make: "Ford",
    model: "Mustang (1967–1968)",
    era: "1967–1968",
    metaTitle: "1967 Ford Mustang Hire: Fastback, Coupé, Specs, and Cultural Story",
    metaDescription:
      "Reference guide to the 1967–68 Ford Mustang: fastback, coupé and convertible bodies, V8 engine options, what it's like to drive, and what occasions it suits for hire.",
    tldr:
      "The 1967–68 Ford Mustang is the era most people picture when they think 'classic Mustang': longer, wider, and more muscular than the 1964½–66 cars, with a fastback body that became iconic through Steve McQueen's Bullitt. Available as coupé, convertible, and 2+2 fastback with 200 cu in six and 289 / 390 / 428 V8 options.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/5681/legacy-27902_1280w.webp",
      alt: "Highland Green 1967 Ford Mustang fastback on a city street",
    },
    specs: [
      { label: "Years (this generation)", value: "1967–1968" },
      { label: "Body styles", value: "Hardtop coupé, convertible, 2+2 fastback" },
      { label: "Base engine", value: "3.3 L (200 cu in) inline-six, 120 bhp" },
      { label: "V8 options", value: "4.7 L (289), 4.9 L (302 in '68), 6.4 L (390), 7.0 L (428 CJ in '68)" },
      { label: "Transmission", value: "3- or 4-speed manual, 3-speed C4 automatic" },
      { label: "Drivetrain", value: "Front engine, rear-wheel drive" },
      { label: "Notable", value: "First Mustang to fit a big-block (390 / 428)" },
    ],
    sections: [
      {
        heading: "History",
        body:
          "The 1967 redesign was the first major facelift of the original Mustang. The body grew in every direction to accommodate Ford's FE-series big-block V8s. 1968 was largely a carry-over with detail changes, including federal safety items, side marker lights, and the new 302 small-block. The 1968½ 428 Cobra Jet, introduced mid-year, gave Mustang its first genuinely fast factory engine.",
      },
      {
        heading: "What it's like to drive",
        body:
          "A small-block 289/302 Mustang is friendly: light steering (with power assist on most), a torquey V8, and a soft ride that suits American boulevard driving better than tight European roads. Drum brakes (discs were optional) reward gentle planning. Big-block 390 and 428 cars feel heavy on the nose and are best on long, straight roads. All of them sound excellent and look the part for any 1960s–1970s setting.",
      },
      {
        heading: "What occasions it suits",
        body:
          "The 1967–68 Mustang is the second most-requested 'movie car' on the platform after the Aston Martin DB5. Highland Green fastbacks are booked year-round for film and TV work, prom and high-school events, music videos, and bachelor / stag celebrations. Convertibles are popular for weddings in warmer markets (Cote d'Azur, LA, Sydney).",
      },
    ],
    culture: [
      "Steve McQueen drove a Highland Green 1968 fastback through the streets of San Francisco in Bullitt (1968). Its car chase is arguably the most-quoted in cinema history.",
      "A 1967 fastback features in Gone in 60 Seconds (2000) as 'Eleanor'.",
      "The Mustang nameplate, launched April 1964, sold over a million units in 18 months and gave the 'pony car' segment its name.",
    ],
    occasions: [
      "Film & TV picture car (US 1960s–70s settings)",
      "Wedding transport (convertibles especially)",
      "Prom and graduation hire",
      "Music videos and fashion shoots",
      "Bachelor / bachelorette day rentals",
    ],
    faqs: [
      {
        q: "What does it cost to hire a 1967 Mustang?",
        a: "Day rates typically range from £350 to £900 in Europe and US$400 to US$1,100 in North America, depending on body style, engine, and whether the car has been restored or is a survivor.",
      },
      {
        q: "Is a Mustang fastback hard to drive?",
        a: "No. A 289/302 small-block Mustang is one of the easier classic muscle cars to drive. Big-block 390 and 428 cars need more respect and are usually offered with the owner's brief before you set off.",
      },
      {
        q: "Are 'Eleanor' or 'Bullitt' replicas available on BookAclassic?",
        a: "Yes, several owners offer tribute / replica builds. Listings should disclose that the car is a tribute, not an original. If authenticity matters (e.g. film production), filter to original-spec cars.",
      },
    ],
    listingsQuery: { make: "Ford", q: "Mustang" },
    related: {
      models: ["porsche-911-air-cooled"],
      useCases: ["film-and-tv-car-hire"],
      cities: ["cote-dazur", "london"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "porsche-911-air-cooled",
    make: "Porsche",
    model: "911 (air-cooled)",
    era: "1964–1998",
    metaTitle: "Air-Cooled Porsche 911 Hire: 901 to 993 Generations Explained",
    metaDescription:
      "Reference guide to the air-cooled Porsche 911 generations (1964–1998): 901, F-Series, G-Series, 964, 993. Specs, what it's like to drive, and what occasions it suits.",
    tldr:
      "Air-cooled Porsche 911s, including the 901, F-Series, G-Series, 964, and 993, are the most coveted sports cars on the platform. They combine a rear engine, flat-six, and mechanical feel. The 993 (1993–1998) is the last and most refined air-cooled 911. These cars suit driving roads and weekend tours rather than ceremonies or chauffeured airport transfers.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/3370/legacy-54397_1280w.webp",
      alt: "Air-cooled Porsche 911 on a mountain pass",
    },
    specs: [
      { label: "Generations (air-cooled)", value: "901 (1964–65), F (1968–73), G (1974–89), 964 (1989–94), 993 (1994–98)" },
      { label: "Engine", value: "Flat-six, naturally aspirated or turbocharged (930 / 964 / 993 Turbo)" },
      { label: "Displacement", value: "2.0 L to 3.8 L across generations" },
      { label: "Drivetrain", value: "Rear engine, rear-wheel drive (964 / 993 Carrera 4 added AWD)" },
      { label: "Body styles", value: "Coupé, Targa, Cabriolet, Speedster" },
      { label: "Top speed", value: "≈ 130 mph (2.0) to 180+ mph (993 Turbo)" },
    ],
    sections: [
      {
        heading: "Which air-cooled 911 should I hire?",
        body:
          "F-Series (1968–73): the most analogue and the most valuable; mechanical injection cars (S, RS) need a warm engine before they feel right. G-Series (1974–89): the easiest to live with, especially 3.2 Carrera (1984–89) with its G50 gearbox. 964 (1989–94): looks classic, drives almost modern; power steering and ABS on most. 993 (1993–98): the last air-cooled; best brakes, best ride, smoothest engine.",
      },
      {
        heading: "What it's like to drive",
        body:
          "All air-cooled 911s share the rear-engine fingerprint: light front end, strong traction, an engine note that builds metallically to 6,500 rpm. The early cars (pre-915 gearbox) have a slow, deliberate shift. The G50 (1987+) and 964/993 boxes are crisp. None are wide cars; all fit British country lanes better than a modern 911 does.",
      },
      {
        heading: "What occasions it suits",
        body:
          "Driving weekends (Alps, Highlands, Pyrenees, Cotswolds), birthday self-drive experiences, photo shoots, and track-day support work. They are less common for weddings because the cabin is small and the suspension is firm. Targas and Cabriolets see more event work than coupés.",
      },
    ],
    culture: [
      "The 911 has been continuously produced since 1964, making it one of the longest unbroken model lines in automotive history.",
      "Steve McQueen's 1969 911S features in the opening sequence of Le Mans (1971).",
      "Magnus Walker, Singer, and RUF have built entire reputations on air-cooled 911 reinterpretations.",
    ],
    occasions: [
      "Driving roads weekend (self-drive)",
      "Milestone-birthday gift experiences",
      "Photo shoots and lifestyle content",
      "Track day support / paddock car",
    ],
    faqs: [
      {
        q: "What does an air-cooled 911 cost to hire?",
        a: "Day rates run from £350 (G-Series 3.2 Carrera) to £1,500+ (993 Turbo, RS-spec cars). 964 and 993 sit in the £400–£800 band.",
      },
      {
        q: "Are these cars insured for spirited driving?",
        a: "Self-drive bookings are insured to BookAclassic's standard rental cover, which includes ordinary fast road use. Track use is excluded unless the listing says otherwise.",
      },
      {
        q: "Manual or Tiptronic?",
        a: "We strongly recommend manual for the air-cooled experience. Tiptronic 964 and 993 cars exist but feel slower and less engaging.",
      },
    ],
    listingsQuery: { make: "Porsche", q: "911" },
    related: {
      models: ["jaguar-e-type", "mercedes-pagoda"],
      useCases: ["driving-experience-hire"],
      cities: ["cotswolds", "cote-dazur"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "volkswagen-beetle",
    make: "Volkswagen",
    model: "Beetle (Type 1)",
    era: "1938–2003",
    metaTitle: "Classic VW Beetle Hire: History, Specs, and What It's Like to Drive",
    metaDescription:
      "Reference guide to the classic air-cooled Volkswagen Beetle (Type 1): history, generations, what it's like to drive, and the kinds of events and shoots it suits.",
    tldr:
      "The air-cooled Volkswagen Beetle (Type 1, 1938–2003) is the most-produced single car platform in history at over 21 million units. Rear-engined, simple, and instantly recognisable. Suited to relaxed weddings, photo shoots, festivals, and short scenic drives rather than fast motorway work.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/3233/legacy-15110_1280w.webp",
      alt: "Pastel-coloured classic VW Beetle on a coastal road",
    },
    specs: [
      { label: "Years produced", value: "1938–2003 (last cars built in Mexico)" },
      { label: "Engine", value: "Air-cooled flat-four, 1.1 L to 1.6 L" },
      { label: "Power", value: "25 bhp (early) to ~50 bhp (1600)" },
      { label: "Drivetrain", value: "Rear engine, rear-wheel drive, 4-speed manual" },
      { label: "Top speed", value: "≈ 60–80 mph depending on engine" },
      { label: "Body styles", value: "2-door saloon, cabriolet (Karmann)" },
    ],
    sections: [
      {
        heading: "History",
        body:
          "Designed by Ferdinand Porsche in the late 1930s as the German 'people's car', mass production began in earnest after 1945 under British military oversight. The Beetle became the symbol of post-war reconstruction in Germany, then a cultural icon in 1960s America. Production continued in Mexico until 2003, making the Type 1 the longest-produced single car platform.",
      },
      {
        heading: "What it's like to drive",
        body:
          "Slow, friendly, and unintimidating. The engine sits behind the rear axle, so steering is light and traction is good in the wet. Brakes are drum on most cars; expect to plan stops. Cabriolets are particularly popular for weddings because of the open layout and forgiving pace.",
      },
      {
        heading: "What occasions it suits",
        body:
          "Casual weddings, festival transport, surf-and-vintage photo shoots, anniversary gifts, and short-distance scenic drives. Not appropriate for long motorway transfers or anyone in a hurry.",
      },
    ],
    culture: [
      "Featured in Disney's Herbie films as 'Herbie the Love Bug' (1968 onwards).",
      "Adopted by 1960s American counterculture; one of the first foreign cars to sell in serious volume in the US.",
      "The 'Bug' nickname is so universal it features in dictionaries.",
    ],
    occasions: [
      "Casual or beach weddings (cabriolet)",
      "Festival and surf-culture photo shoots",
      "Birthday and anniversary gifts",
      "Short scenic drives in fair weather",
    ],
    faqs: [
      {
        q: "How much does it cost to hire a classic Beetle?",
        a: "Day rates typically run £150–£400 across Europe; cabriolets and concours examples sit at the top of that band.",
      },
      {
        q: "Can a Beetle do a long-distance trip?",
        a: "It can, but plan for a relaxed pace (60–65 mph cruise) and short daily distances. Modern motorway traffic feels much faster than a classic Beetle does.",
      },
    ],
    listingsQuery: { make: "Volkswagen", q: "Beetle" },
    related: {
      models: ["mini-classic", "fiat-500-classic"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      cities: ["paris", "rome"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "mgb",
    make: "MG",
    model: "MGB",
    era: "1962–1980",
    metaTitle: "MGB Hire: The Definitive British Affordable Classic Roadster",
    metaDescription:
      "Reference guide to the MGB roadster and GT (1962–1980): specs, chrome- vs rubber-bumper differences, what it's like to drive, and the occasions it suits.",
    tldr:
      "The MGB (1962–1980) is the most-produced British classic roadster, with over half a million units. A simple, robust pushrod four-cylinder, a tight monocoque body, and a tail-happy chassis. Cheap to hire, easy to drive, and a staple of summer weddings and short scenic drives.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/5364/legacy-41195_1280w.webp",
      alt: "British Racing Green MGB roadster",
    },
    specs: [
      { label: "Years produced", value: "1962–1980" },
      { label: "Body styles", value: "Roadster, MGB GT (1965–80), V8 GT (1973–76)" },
      { label: "Engine", value: "1.8 L BMC B-Series I4 (most), 3.5 L Rover V8 (GT V8)" },
      { label: "Power", value: "≈ 95 bhp (1.8 four) / 137 bhp (V8)" },
      { label: "Bumpers", value: "Chrome (1962–74) and rubber (1974–80); chrome is the desirable look" },
      { label: "Drivetrain", value: "Front engine, RWD, 4-speed manual (overdrive optional)" },
    ],
    sections: [
      {
        heading: "Chrome vs rubber bumper",
        body:
          "1974–80 cars received raised ride height and rubber bumpers to meet US crash standards, which hurt the handling and looks. Chrome-bumper cars (1962–74) are more sought-after and what most renters expect.",
      },
      {
        heading: "What it's like to drive",
        body:
          "Light, simple, and forgiving. The 1.8 engine has plenty of low-down torque, the gearbox is quick (especially with overdrive), and the steering is communicative. Brakes are adequate, not great. Roadsters get loud above 60 mph but stay comfortable on country roads.",
      },
      {
        heading: "What occasions it suits",
        body:
          "Smaller weddings, anniversary gifts, day-trip hires, and budget-friendly classic-car-day experiences. A good first classic to drive for someone unfamiliar with old cars.",
      },
    ],
    culture: [
      "Over 500,000 MGBs were produced, making it the best-selling British sports car of all time.",
      "A staple of British classic car shows and TV (Inspector Morse, Heartbeat).",
    ],
    occasions: [
      "Wedding car (smaller, informal ceremonies)",
      "First-classic driving experiences",
      "Anniversary and birthday gifts",
      "Photo shoots needing a 'British countryside' look",
    ],
    faqs: [
      {
        q: "How much does it cost to hire an MGB?",
        a: "Day rates typically run £150–£350. MGB GT V8 and concours roadsters sit higher.",
      },
      {
        q: "Roadster or GT?",
        a: "Roadster for weddings and photo shoots. GT for two- or three-day touring, especially in unpredictable weather.",
      },
    ],
    listingsQuery: { make: "MG", q: "MGB" },
    related: {
      models: ["jaguar-e-type", "mini-classic"],
      useCases: ["wedding-car-hire", "anniversary-gift-experience"],
      cities: ["london", "cotswolds"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "mini-classic",
    make: "Mini",
    model: "Classic Mini",
    era: "1959–2000",
    metaTitle: "Classic Mini Hire: Mk I to Mk VII, Cooper, and Cultural Story",
    metaDescription:
      "Reference guide to the classic Mini (1959–2000): generations, Cooper variants, what it's like to drive, and what occasions a classic Mini hire suits.",
    tldr:
      "The classic Mini (1959–2000) is a 3.05 m long front-wheel-drive package that redefined small-car design. A simple, transverse-engine four-cylinder, go-kart handling, and a cultural footprint out of all proportion to its size. Best for city work, weddings, photo shoots, and short B-road drives.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/1030765/legacy-1034337_1280w.webp",
      alt: "Red classic Mini Cooper with white roof",
    },
    specs: [
      { label: "Years produced", value: "1959–2000" },
      { label: "Engine", value: "Transverse 0.85 / 1.0 / 1.1 / 1.3 L BMC A-Series I4" },
      { label: "Cooper / Cooper S", value: "Tuned 970–1275 cc, twin SU carbs, more power and disc front brakes" },
      { label: "Drivetrain", value: "Front engine, front-wheel drive, 4-speed manual (gearbox in sump)" },
      { label: "Length", value: "3.05 m (≈ 10 ft)" },
    ],
    sections: [
      {
        heading: "History",
        body:
          "Designed by Sir Alec Issigonis at BMC and launched in 1959 in response to the Suez fuel crisis. The transverse engine and gearbox-in-sump layout freed up cabin space and became the template for almost every small front-wheel-drive car since. Cooper variants from 1961 added performance credibility, culminating in three outright Monte Carlo Rally wins (1964, 1965, 1967).",
      },
      {
        heading: "What it's like to drive",
        body:
          "Direct steering, tiny dimensions, and a cabin where the wheel is almost in your lap. The 1275 Cooper S is genuinely quick along a B-road; smaller engines are happy enough in town. Heavy in the city without power steering, and very noisy on the motorway.",
      },
    ],
    culture: [
      "Won the Monte Carlo Rally outright in 1964, 1965, and 1967 with John Cooper–prepared cars.",
      "Driven by Mr Bean (Rowan Atkinson) in the BBC series.",
      "Central to the heist plot of The Italian Job (1969 and 2003).",
    ],
    occasions: [
      "City weddings (especially London, Rome, Paris)",
      "Photo shoots and music videos",
      "Birthday driving days",
      "Promotional or branded fleet hire",
    ],
    faqs: [
      {
        q: "How much does it cost to hire a classic Mini?",
        a: "Day rates run £120–£300. Cooper S and concours-grade Mk I cars cost more.",
      },
      {
        q: "Can two adults fit in the back?",
        a: "Two adults will fit on short hops (under an hour). For longer drives, treat the back as 2+2 luggage space.",
      },
    ],
    listingsQuery: { make: "Mini" },
    related: {
      models: ["volkswagen-beetle", "fiat-500-classic", "mgb"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      cities: ["london", "rome", "paris"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "fiat-500-classic",
    make: "Fiat",
    model: "500 (Nuova / Cinquecento)",
    era: "1957–1975",
    metaTitle: "Classic Fiat 500 Hire: Nuova, F, L, and R Variants Explained",
    metaDescription:
      "Reference guide to the classic Fiat 500 (1957–1975): variants, what it's like to drive, and why it's the favourite wedding and photo-shoot car of Italian cities.",
    tldr:
      "The Fiat 500 'Nuova' (1957–1975) is the smallest, most photogenic, and most distinctly Italian classic on the platform. A rear-engined two-cylinder town car designed for Italy's post-war reconstruction. The default wedding and lifestyle car of Rome, Florence, and the Amalfi Coast.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/1041638/legacy-1110716_1280w.webp",
      alt: "Classic Fiat 500 parked in a narrow Italian street",
    },
    specs: [
      { label: "Years produced", value: "1957–1975" },
      { label: "Engine", value: "479 / 499 / 594 cc air-cooled inline-twin" },
      { label: "Power", value: "13 to 23 bhp" },
      { label: "Drivetrain", value: "Rear engine, rear-wheel drive, 4-speed unsynchronised (or synchro on R)" },
      { label: "Variants", value: "Nuova, D, F, L (Lusso), R (Rinnovata), Giardiniera (estate)" },
      { label: "Length", value: "2.97 m" },
    ],
    sections: [
      {
        heading: "What it's like to drive",
        body:
          "Charming and very slow. The two-cylinder engine settles into a putter at 50 km/h and protests above 80 km/h. The folding canvas roof and tiny dimensions make it perfect for narrow Italian alleys and for being photographed.",
      },
      {
        heading: "What occasions it suits",
        body:
          "Italian weddings, fashion shoots, hen and stag city tours, anniversary gifts. Not appropriate for motorway use or for tall passengers over long distances.",
      },
    ],
    culture: [
      "Featured in countless Italian films and tourism campaigns; a permanent fixture in MoMA's design collection.",
      "Often called the 'Cinquecento' or 'Bambino' in everyday Italian.",
    ],
    occasions: [
      "Italian city weddings (Rome, Florence, Naples)",
      "Fashion and lifestyle photo shoots",
      "Small-group sightseeing tours",
    ],
    faqs: [
      {
        q: "How much does it cost to hire a classic Fiat 500?",
        a: "Day rates run €120–€280 across Italy and slightly more in northern Europe.",
      },
      {
        q: "Is it safe in modern traffic?",
        a: "On urban and rural roads at sensible speeds, yes. We don't recommend motorway use; most owners restrict the cars to ≤80 km/h roads.",
      },
    ],
    listingsQuery: { make: "Fiat", q: "500" },
    related: {
      models: ["volkswagen-beetle", "mini-classic"],
      useCases: ["wedding-car-hire"],
      cities: ["rome", "tuscany"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "aston-martin-db5",
    make: "Aston Martin",
    model: "DB5",
    era: "1963–1965",
    metaTitle: "Aston Martin DB5 Hire: The James Bond Car, Specs and Hire Notes",
    metaDescription:
      "Reference guide to the Aston Martin DB5 (1963–1965): specs, cultural story, what it's like to drive, and what occasions a DB5 (or faithful tribute) suits for hire.",
    tldr:
      "The Aston Martin DB5 (1963–1965) is the most culturally famous British grand tourer, immortalised by Goldfinger (1964). A 4.0 L straight-six, 282 bhp, hand-rolled aluminium body. Genuine cars are concours-only; many owners offer DB5 tribute / replica builds for hire instead.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/1028981/legacy-1023365_1280w.webp",
      alt: "Silver Birch Aston Martin DB5",
    },
    specs: [
      { label: "Years produced", value: "1963–1965 (≈ 1,059 built)" },
      { label: "Engine", value: "4.0 L Tadek Marek DOHC straight-six" },
      { label: "Power", value: "282 bhp (Vantage: 314 bhp)" },
      { label: "Body styles", value: "Coupé, Convertible (Volante), Shooting Brake (12 built)" },
      { label: "Top speed", value: "≈ 145 mph" },
      { label: "Drivetrain", value: "Front engine, RWD, ZF 5-speed manual (BorgWarner auto optional)" },
    ],
    sections: [
      {
        heading: "Originals vs tributes",
        body:
          "Genuine DB5s are worth high six- to seven-figure sums and are almost never offered for self-drive. Most BookAclassic hires are well-built tributes or replicas. They typically use a DB6 floorpan or a custom chassis, an original-spec straight-six, and faithful body panels. Listings should disclose this clearly.",
      },
      {
        heading: "What it's like to drive",
        body:
          "Long, deliberate gearshifts, heavy unassisted steering (power assist optional), a noisy and slightly highly-strung straight-six. Best at touring pace on open A-roads. Modern bumpers and small mirrors make town driving harder than it looks.",
      },
    ],
    culture: [
      "Driven by Sean Connery's James Bond in Goldfinger (1964) and Thunderball (1965); reappeared in GoldenEye, Skyfall, Spectre, and No Time to Die.",
      "Frequently described as 'the most famous car in the world'.",
    ],
    occasions: [
      "James Bond–themed events and stag/hen parties",
      "Corporate film and TV shoots",
      "Premium weddings (chauffeured)",
      "Concours appearances",
    ],
    faqs: [
      {
        q: "Can I really hire a Goldfinger DB5?",
        a: "Original cars are extremely rare to hire. Most listings are tribute builds. If you need an original for film or editorial, contact the owner directly and confirm provenance.",
      },
      {
        q: "How much to hire one?",
        a: "Tributes are £800–£1,500/day chauffeured. Original cars when offered start around £2,500/day and are usually chauffeur-only.",
      },
    ],
    listingsQuery: { make: "Aston Martin", q: "DB5" },
    related: {
      models: ["jaguar-e-type", "rolls-royce-silver-shadow"],
      useCases: ["wedding-car-hire", "film-and-tv-car-hire"],
      cities: ["london"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "rolls-royce-silver-shadow",
    make: "Rolls-Royce",
    model: "Silver Shadow",
    era: "1965–1980",
    metaTitle: "Rolls-Royce Silver Shadow Hire: Wedding & Chauffeur Hire Notes",
    metaDescription:
      "Reference guide to the Rolls-Royce Silver Shadow (1965–1980): variants, what it's like to ride in, and why it's the dominant chauffeur-driven wedding car in the UK and US.",
    tldr:
      "The Rolls-Royce Silver Shadow (1965–1980) is the most-produced Rolls-Royce ever (≈ 32,000 units) and the default chauffeured classic wedding car in the UK, Ireland, and US. Hydropneumatic suspension, a 6.2 / 6.75 L V8, and a cabin designed around the rear-seat passenger.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/9866/legacy-96146_1280w.webp",
      alt: "White Rolls-Royce Silver Shadow outside a church",
    },
    specs: [
      { label: "Years produced", value: "1965–1980 (Silver Shadow II from 1977)" },
      { label: "Engine", value: "6.2 L (early) / 6.75 L (1970+) Rolls-Royce L-Series V8" },
      { label: "Drivetrain", value: "Front engine, RWD, GM-licensed 3- or 4-speed automatic" },
      { label: "Body styles", value: "Saloon (most common), Coupé / Corniche, Long-Wheelbase" },
      { label: "Notable features", value: "Citroën-licensed hydropneumatic self-levelling rear, all-disc brakes" },
    ],
    sections: [
      {
        heading: "Why it's the default wedding car",
        body:
          "Long rear doors, deep upholstery, walnut and leather, and a stately ride. Most Silver Shadows in BookAclassic's UK fleet are white or silver and offered exclusively chauffeur-driven for weddings. Owners often include ribbons, an umbrella, and a non-alcoholic drink for the couple in the booking.",
      },
      {
        heading: "What it's like to ride in",
        body:
          "The rear cabin is the point. The V8 is silent at urban speeds; the ride floats over rough roads. Self-drive offers exist but are less common because owners prefer to chauffeur for insurance and presentation reasons.",
      },
    ],
    culture: [
      "Used by countless heads of state, celebrities, and royal weddings.",
      "The Silver Shadow II was the last 'Crewe-engineered' Rolls before BMW-era investment changed the brand.",
    ],
    occasions: [
      "Weddings (chauffeur-driven)",
      "Anniversary and milestone-birthday transport",
      "Corporate event arrival",
      "Funerals (specific listings)",
    ],
    faqs: [
      {
        q: "How much to hire a Silver Shadow chauffeured?",
        a: "UK wedding-day rates run £400–£900 including chauffeur, ribbons, and 3–4 hours of cover. Longer days and multi-car packages cost more.",
      },
      {
        q: "Self-drive available?",
        a: "Occasionally, subject to age (35+) and licence checks. Most owners restrict the Shadow to chauffeur-only.",
      },
    ],
    listingsQuery: { make: "Rolls-Royce", q: "Silver Shadow" },
    related: {
      models: ["aston-martin-db5"],
      useCases: ["wedding-car-hire"],
      cities: ["london"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "mercedes-pagoda",
    make: "Mercedes-Benz",
    model: "SL 'Pagoda' (W113)",
    era: "1963–1971",
    metaTitle: "Mercedes-Benz Pagoda SL (W113) Hire: 230 / 250 / 280 SL Explained",
    metaDescription:
      "Reference guide to the Mercedes-Benz W113 'Pagoda' SL (1963–1971): 230 SL, 250 SL, 280 SL differences, what it's like to drive, and what occasions it suits.",
    tldr:
      "The Mercedes-Benz W113 'Pagoda' SL (1963–1971) is a German grand tourer with a Paul Bracq–designed body and a distinctive concave 'pagoda' hardtop. Built in 230 SL, 250 SL, and 280 SL forms; the 280 SL with automatic is the most relaxed daily-driveable classic on the platform.",
    hero: {
      image: "https://pub-b0c8d82c3e244a2fbf1133bf122e76fa.r2.dev/cars/1026840/legacy-1214693_1280w.webp",
      alt: "Light blue Mercedes-Benz Pagoda SL with hardtop",
    },
    specs: [
      { label: "Years produced", value: "1963–1971" },
      { label: "Engines", value: "2.3 L (230 SL), 2.5 L (250 SL), 2.8 L (280 SL); all are fuel-injected SOHC sixes" },
      { label: "Power", value: "150–170 bhp" },
      { label: "Body", value: "2-door roadster with removable hardtop (the 'pagoda')" },
      { label: "Transmission", value: "4-speed manual, 5-speed ZF (rare), or 4-speed automatic" },
    ],
    sections: [
      {
        heading: "230, 250, or 280 SL?",
        body:
          "230 SL (1963–67): lightest and purest. 250 SL (1966–68): rare, longer-stroke engine, disc brakes all round. 280 SL (1968–71): most powerful and smoothest, and the most produced for the US market. It is the easiest one to hire.",
      },
      {
        heading: "What it's like to drive",
        body:
          "The Pagoda is heavier and more stable than an E-Type, with a less sporting feel. The automatic 280 SL is one of the best classic 'cruise' cars on the platform: quiet, comfortable, and reasonably quick. Swapping the hardtop is a two-person job. Most owners keep it on for transfers and remove it for ceremonies.",
      },
    ],
    culture: [
      "Featured on the cover of countless travel magazines; favoured by 1960s European film stars.",
      "Often nicknamed the 'California Coupé' when configured with the optional hardtop and no soft-top.",
    ],
    occasions: [
      "Mature / second-marriage weddings",
      "Cote d'Azur and Tuscany touring",
      "Photo shoots and lifestyle content",
      "Anniversary gifts (chauffeured or self-drive)",
    ],
    faqs: [
      {
        q: "Day rate for a Pagoda?",
        a: "£400–£900 across Europe; concours 230 SL and rare 250 SL cars are higher.",
      },
      {
        q: "Can it do a week-long European trip?",
        a: "Yes. The Pagoda is one of the better long-distance classics. Plan for ≤300 miles/day and check the owner's preferred touring radius first.",
      },
    ],
    listingsQuery: { make: "Mercedes-Benz", q: "Pagoda" },
    related: {
      models: ["jaguar-e-type", "porsche-911-air-cooled"],
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      cities: ["cote-dazur", "tuscany"],
    },
    updatedAt: UPDATED,
  },
];

export function getModelGuide(slug: string): ModelGuide | undefined {
  return MODEL_GUIDES.find((m) => m.slug === slug);
}
